import { memo, ReactElement, useCallback } from 'react';
import { Grid, CardActions, Typography, CardContent, Tooltip, Card } from '@mui/material';
import Functionalities from 'system/components/groups/form/functionality';
import { PmiButton } from 'shell/components/shared/button';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { FunctionalityVM } from 'system/view-models/functionality';
import Groups from './groups';
import PmiTextField from 'shell/components/shared/text-field';
import { GroupVM } from 'system/view-models/group';

const validationSchema = yup.object({
  name: yup.string().nullable().required('Il campo è obbligatorio'),
});

export interface FormProfile {
  name?: string;
  code?: string;
  lastUpdate?: string;
  functionalities?: Array<FunctionalityVM>;
  groups?: Array<GroupVM>;
}

interface FormProfileProps {
  onOk: (form?: FormProfile) => void;
  onCancel: (form?: FormProfile) => void;
  disabled?: boolean;
  insert?: boolean;
  initialValues?: FormProfile;
  title: string;
}

const DetailProfileComponent = ({
  onOk,
  onCancel,
  disabled = false,
  insert,
  initialValues,
  title,
}: FormProfileProps): ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const profileForm = useFormik<Partial<FormProfile>>({
    initialValues: initialValues || {
      name: '',
    },
    validationSchema: validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const onChangeFunctionalities = useCallback(
    (functions: Array<FunctionalityVM>) => {
      const functionalities: Array<FunctionalityVM> = functions;
      profileForm.setFieldValue('functionalities', functionalities);
    },
    [profileForm.setFieldValue]
  );

  const onChangeGroups = useCallback(
    (groups: Array<GroupVM>) => {
      const grp: Array<GroupVM> = groups;
      profileForm.setFieldValue('groups', grp);
    },
    [profileForm.setFieldValue]
  );

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={profileForm.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <Tooltip arrow title="Il campo è generato automaticamente">
                  <div>
                    <PmiTextField
                      label="ID Profilo"
                      aria-label="ID profilo"
                      id="code"
                      name="code"
                      fullWidth
                      type="text"
                      value={profileForm.values.code || ''}
                      onChange={profileForm.handleChange}
                      disabled
                      variant="outlined"
                    />
                  </div>
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  error={profileForm.touched.name && Boolean(profileForm.errors.name)}
                  helperText={profileForm.errors.name}
                  label="Nome Profilo *"
                  aria-label="Nome profilo"
                  inputProps={{ maxLength: '50' }}
                  id="name"
                  name="name"
                  fullWidth
                  type="text"
                  value={profileForm.values.name || ''}
                  onChange={profileForm.handleChange}
                  disabled={disabled}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PmiTextField
                  aria-label="contesto ultimo aggiornamento"
                  label="Contesto ultimo aggiornamento"
                  id="lastUpdate"
                  name="lastUpdate"
                  fullWidth
                  inputProps={{ maxLength: '50' }}
                  variant="outlined"
                  value={profileForm.values.lastUpdate || ''}
                  onChange={profileForm.handleChange}
                  disabled={disabled}
                  error={profileForm.touched.lastUpdate && Boolean(profileForm.errors.lastUpdate)}
                  helperText={profileForm.errors.lastUpdate}
                />
              </Grid>
            </Grid>
            <Functionalities
              insert={insert}
              functionalities={profileForm.values.functionalities || []}
              disabled={disabled}
              onChange={onChangeFunctionalities}
            />
            <Groups
              insert={insert}
              groups={profileForm.values.groups || []}
              disabled={disabled}
              onChange={onChangeGroups}
            />
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton variant="contained" color="primary" type="submit" disabled={disabled}>
            Conferma
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel(profileForm.values);
            }}
          >
            Indietro
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

DetailProfileComponent.displayName = 'Operator new page component';

const DetailProfile = memo(DetailProfileComponent);

export default DetailProfile;
